/*
 * Hover-to-play for product gallery videos (PDP).
 *
 * Requirement: on a video media in the product gallery, hovering plays our mp4
 * (muted, looping) over the poster so the "screen" comes alive; moving the mouse
 * away stops it and shows the original static poster image again.
 *
 * How it works: for each `.product-media-container.media-type-video` inside the
 * main <media-gallery>, we reuse the mp4 <source> already present in Shopify's
 * deferred-media <template> to build a muted/looping/inline <video> overlay.
 * mouseenter -> play (overlay fades in over the poster); mouseleave -> pause +
 * rewind + fade out (poster shows). Only runs on hover-capable pointers; touch
 * devices keep the theme's default tap-to-play (deferred-media) untouched.
 *
 * Pure vanilla JS, no dependencies. Included from main-product.liquid. Styling
 * (.pg-hover-video / .pg-hover-playing) lives in section-main-product.css.
 */
(function () {
  'use strict';

  var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');

  function getSources(container) {
    var tpl = container.querySelector('deferred-media template');
    var video = tpl && tpl.content ? tpl.content.querySelector('video') : null;
    if (!video) return [];
    return Array.prototype.map
      .call(video.querySelectorAll('source'), function (s) {
        return { src: s.getAttribute('src'), type: s.getAttribute('type') || 'video/mp4' };
      })
      .filter(function (s) { return s.src; });
  }

  function setup(container) {
    if (container.__pgHoverInit) return;
    var sources = getSources(container);
    if (!sources.length) return; // no playable mp4 — leave default behaviour
    container.__pgHoverInit = true;

    var video = null;
    var hovering = false;

    function reveal() {
      // Only show the overlay once a frame is actually rendering and the pointer
      // is still over the media — avoids a black flash on the first (unbuffered) hover.
      if (hovering) container.classList.add('pg-hover-playing');
    }

    function build() {
      video = document.createElement('video');
      video.className = 'pg-hover-video';
      // Match the poster's fit so the overlay lines up exactly.
      video.style.objectFit = container.classList.contains('media-fit-cover') ? 'cover' : 'contain';
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', 'none');
      video.setAttribute('tabindex', '-1');
      video.setAttribute('aria-hidden', 'true');
      video.addEventListener('playing', reveal);
      sources.forEach(function (s) {
        var el = document.createElement('source');
        el.src = s.src;
        el.type = s.type;
        video.appendChild(el);
      });
      container.appendChild(video);
    }

    function enter() {
      if (!hoverMQ.matches) return;
      hovering = true;
      if (!video) build();
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
      // Already buffered (re-hover): show immediately; otherwise wait for 'playing'.
      if (video.readyState >= 3) reveal();
    }

    function leave() {
      hovering = false;
      container.classList.remove('pg-hover-playing');
      if (video) {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
      }
    }

    container.addEventListener('mouseenter', enter);
    container.addEventListener('mouseleave', leave);
  }

  function init() {
    if (!hoverMQ.matches) return; // touch / no-hover: keep default tap-to-play
    var galleries = document.querySelectorAll('media-gallery');
    Array.prototype.forEach.call(galleries, function (g) {
      var vids = g.querySelectorAll('.product-media-container.media-type-video');
      Array.prototype.forEach.call(vids, setup);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Gallery can be injected late (variant change / theme editor); re-scan.
  window.addEventListener('load', init);
})();
